import React from 'react'
import PropTypes from 'prop-types'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { withEdgeErrorBoundary } from 'components/common/with-edge-error-boundary'
import { WidgetFallback } from 'components/common/widget-fallback'
import { useTranslation } from 'react-i18next'
import { MetricTypes } from 'utils/constants'
import { useFormatMetricValue } from 'components/common/hooks/useFormatMetricValue'
import classNames from 'classnames/bind'
import styles from './StockAndDeliveriesChart.module.scss'
import moment from 'moment'
import { isHexColourCode } from 'utils/colour'
import { ReactComponent as TruckIcon } from 'images/analytics/delivery-truck.svg'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  ResponsiveContainer,
  Label,
  LabelList,
} from 'recharts'

const cx = classNames.bind(styles)

const FUEL_IN_TANK = 'fuelInTank'
const DELIVERY = 'delivery'
const STOCK_VALUE = 'fuelValueInStock'
const SOLD = 'fuelVolumeSold'

const TOOLTIP_METRICS = [
  { key: FUEL_IN_TANK, labelKey: 'widgets.analytics.fuelInTankAtEndOfDay', metricType: MetricTypes.VOLUME },
  { key: STOCK_VALUE, labelKey: 'widgets.analytics.valueOfFuelAtEndOfDay', metricType: MetricTypes.PROFIT },
  { key: SOLD, labelKey: 'widgets.analytics.volumeSoldDuringTheDay', metricType: MetricTypes.VOLUME },
  { key: DELIVERY, labelKey: 'widgets.analytics.deliveredDuringTheDay', metricType: MetricTypes.VOLUME },
]

// Each series wears the brand colour of the fuel grade it represents; the
// legacy green stands in when a grade arrives without a usable colour.
const seriesColour = (colour) =>
  isHexColourCode(colour) ? colour : styles.fuelInTank

// Below this bar width the per-tank label under the axis becomes unreadable, so
// it is dropped and the tooltip carries the tank identification instead.
const MIN_LABELLED_BAR_WIDTH = 14

// The truck marker shrinks with the bar down to this width; any narrower and
// neighbouring markers collide, so the tooltip carries the delivery instead.
const MIN_MARKED_BAR_WIDTH = 6
const MARKER_MAX_WIDTH = 16
const MARKER_ASPECT_RATIO = 16 / 24
const MARKER_GAP = 4

// Prevents delivery truck svg from being clipped
const MARKER_HEADROOM = Math.ceil(MARKER_MAX_WIDTH * MARKER_ASPECT_RATIO) + MARKER_GAP

// The backend serialises every volume as a string ("10000.0") or null.
const parseVolume = (value) => {
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? null : parsed
}

// A truck appears only over bars wide enough to carry it, on days a delivery
// actually landed.
const shouldShowDeliveryMarker = (delivered, width) =>
  Number.isFinite(width) &&
  width >= MIN_MARKED_BAR_WIDTH &&
  (parseVolume(delivered) ?? 0) > 0

const DAILY_TICKS_MAX_DAYS = 14
const WEEKLY_TICKS_MAX_DAYS = 90

// Deterministic x-axis ticks anchored to consistent days, so labels line up
// across weeks instead of drifting with recharts' collision-based spacing:
// every day for short ranges, Mondays for multi-week ranges, and the 1st of
// each month beyond that. The first and last date always render so the range
// boundaries stay legible.
const pickAxisTicks = (dates = []) => {
  if (dates.length <= DAILY_TICKS_MAX_DAYS) {
    return [...dates]
  }

  const isAnchor =
    dates.length <= WEEKLY_TICKS_MAX_DAYS
      ? (date) => moment.utc(date).isoWeekday() === 1
      : (date) => moment.utc(date).date() === 1

  return dates.filter(
    (date, index) => index === 0 || index === dates.length - 1 || isAnchor(date)
  )
}

// Zips the gateway's parallel arrays into one recharts row per date:
// { date, T1_fuelInTank, T1_delivery, T1_fuelValueInStock, T1_fuelVolumeSold, ... }
// Only the fuel in tank at end of day renders as a bar — the delivery drives
// the truck marker, and the rest ride along for the tooltip.
const buildChartRows = ({ dates, tanks } = {}) => {
  if (!dates?.length || !tanks?.length) {
    return []
  }

  return dates.map((date, index) => {
    const row = { date }

    tanks.forEach((tank) => {
      row[`${tank.tankName}_${FUEL_IN_TANK}`] = parseVolume(tank.tankFuelLevel?.[index])
      row[`${tank.tankName}_${DELIVERY}`] = parseVolume(tank.deliveredFuelVolume?.[index])
      row[`${tank.tankName}_${STOCK_VALUE}`] = parseVolume(tank.fuelValueInStock?.[index])
      row[`${tank.tankName}_${SOLD}`] = parseVolume(tank.fuelVolumeSold?.[index])
    })

    return row
  })
}

const StockAndDeliveriesChart = ({ data, grades, dividedByFuelGrade }) => {
  const { t } = useTranslation(['translations'])
  const { formatMetricValue } = useFormatMetricValue()

  const rows = buildChartRows(data)
  const tanks = data?.tanks ?? []
  const axisTicks = pickAxisTicks(data?.dates)

  // Anchored to the bar for the day: y + height is the axis baseline, so the
  // tank name sits just below its own bar.
  const TankLabel = ({ x, y, width, height, tankName }) => {
    if (![x, y, width, height].every(Number.isFinite) || width < MIN_LABELLED_BAR_WIDTH) {
      return null
    }

    return (
      <text
        x={x + width / 2}
        y={y + height + 14}
        textAnchor="middle"
        fill="#b2bfca"
        fontSize={10}
      >
        {tankName}
      </text>
    )
  }

  // A truck above the bar says a load landed during the day.
  // The tooltip carries the delivered quantity the marker cannot convey.
  const DeliveryMarker = ({ x, y, width, value }) => {
    if (![x, y, width].every(Number.isFinite) || !shouldShowDeliveryMarker(value, width)) {
      return null
    }

    const iconWidth = Math.min(width, MARKER_MAX_WIDTH)
    const iconHeight = iconWidth * MARKER_ASPECT_RATIO

    return (
      <TruckIcon
        x={x + (width - iconWidth) / 2}
        y={y - iconHeight - MARKER_GAP}
        width={iconWidth}
        height={iconHeight}
        className={cx('delivery-marker')}
        role="img"
        aria-label={t('widgets.analytics.deliveryLandedDuringTheDay')}
      />
    )
  }

  // Two text rows so the day of the week reads above the date, pushed down to
  // leave room for the tank labels between the axis and the tick.
  const DateTick = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#b2bfca" fontSize={13}>
        <tspan x={0} dy={26}>{moment.utc(payload.value).format('ddd')}</tspan>
        <tspan x={0} dy={16}>{moment.utc(payload.value).format('D MMM')}</tspan>
      </text>
    </g>
  )

  const CustomYAxisTick = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" fill="#b2bfca">
        {formatMetricValue(MetricTypes.VOLUME, payload.value, true, true)}
      </text>
    </g>
  )

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null
    }

    // The row object carries every tank's values, including nulls that recharts
    // may omit from the tooltip payload itself.
    const row = payload[0].payload

    return (
      <div className={cx('tooltip')}>
        <div className={cx('tooltip-date')}>
          <p>{moment.utc(label).format('dddd Do MMM')}</p>
        </div>
        <table className={cx('tooltip-tanks')}>
          <thead>
            <tr>
              <th />
              {tanks.map(({ tankName, colour }) => (
                <th key={tankName} style={{ color: seriesColour(colour) }}>{tankName}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TOOLTIP_METRICS.map(({ key, labelKey, metricType }) => (
              <tr key={key}>
                <th>{t(labelKey)}</th>
                {tanks.map(({ tankName }) => (
                  <td key={tankName}>
                    {formatMetricValue(metricType, row[`${tankName}_${key}`], true, true)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Dividing by fuel grade shows one entry per grade: its swatch followed by
  // its title — a data-driven string, like a station name, so it deliberately
  // bypasses translation; the swatch's meaning lives in a hover tooltip.
  // Dividing by tank shows a single translated entry in the selected grade's
  // colour. Either way, a truck entry explains the delivery markers.
  const legendEntries = dividedByFuelGrade
    ? (grades ?? []).map(({ title, colour }) => ({
        label: title,
        swatches: [
          { colour: seriesColour(colour), tooltip: t('widgets.analytics.fuelInTankAtEndOfDay') },
        ],
      }))
    : [
        {
          label: t('widgets.analytics.fuelInTankAtEndOfDay'),
          swatches: [{ colour: seriesColour(grades?.[0]?.colour) }],
        },
      ]

  const LegendSwatch = ({ colour, tooltip }) => {
    const swatch = (
      <span className={cx('legend-swatch')} style={{ backgroundColor: colour }} />
    )

    if (!tooltip) {
      return swatch
    }

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <BootstrapTooltip className={cx('legend-swatch-tooltip')}>{tooltip}</BootstrapTooltip>
        }
      >
        {swatch}
      </OverlayTrigger>
    )
  }

  const GradeLegend = () => (
    <ul className={cx('legend')}>
      {legendEntries.map(({ label, swatches }) => (
        <li key={label} className={cx('legend-entry')}>
          {swatches.map((swatch) => (
            <LegendSwatch key={swatch.colour} {...swatch} />
          ))}
          {label}
        </li>
      ))}
      <li className={cx('legend-entry')}>
        <TruckIcon className={cx('legend-truck')} width={18} height={12} aria-hidden="true" />
        {t('widgets.analytics.deliveryLandedDuringTheDay')}
      </li>
    </ul>
  )

  return (
    <div className={cx('container')} data-testid="stock-and-deliveries-chart">
      <ResponsiveContainer width="100%" height={600}>
        <BarChart
          className={cx('chart')}
          data={rows}
          margin={{ top: MARKER_HEADROOM, left: 45, bottom: 10 }}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" className={cx('grid')} vertical={false} />
          {/* interval={0} stops recharts thinning the explicit tick list. */}
          <XAxis
            dataKey="date"
            tick={<DateTick />}
            stroke="#eee"
            ticks={axisTicks}
            interval={0}
            height={70}
          />
          <YAxis tick={<CustomYAxisTick />} stroke="none">
            <Label
              value={t('widgets.performanceReport.yAxisLabels.volume')}
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: 'middle', fontSize: '15px' }}
              dx={-44}
            />
          </YAxis>
          <Tooltip
            wrapperStyle={{ outline: 'none' }}
            content={<CustomTooltip />}
            cursor={{ fill: '#f6f8fa' }}
          />
          {tanks.map(({ tankName, colour }) => (
            <Bar
              key={`${tankName}-${FUEL_IN_TANK}`}
              dataKey={`${tankName}_${FUEL_IN_TANK}`}
              fill={seriesColour(colour)}
              maxBarSize={48}
              legendType="none"
              isAnimationActive={false}
            >
              {/* In fuel-grade mode the series carry long grade names that
                  overlap under the axis; the legend identifies them instead. */}
              {!dividedByFuelGrade && (
                <LabelList
                  dataKey={`${tankName}_${FUEL_IN_TANK}`}
                  content={<TankLabel tankName={tankName} />}
                />
              )}
              <LabelList
                dataKey={`${tankName}_${DELIVERY}`}
                content={<DeliveryMarker />}
              />
            </Bar>
          ))}
          <Legend content={<GradeLegend />} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

StockAndDeliveriesChart.propTypes = {
  dividedByFuelGrade: PropTypes.bool,
  grades: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      colour: PropTypes.string,
    })
  ),
  data: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    tanks: PropTypes.arrayOf(
      PropTypes.shape({
        tankName: PropTypes.string,
        colour: PropTypes.string,
        tankFuelLevel: PropTypes.arrayOf(PropTypes.string),
        deliveredFuelVolume: PropTypes.arrayOf(PropTypes.string),
        fuelValueInStock: PropTypes.arrayOf(PropTypes.string),
        fuelVolumeSold: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  }),
}

const FallbackComponent = () => <WidgetFallback />

const ChartWithErrorBoundary = withEdgeErrorBoundary(
  StockAndDeliveriesChart,
  FallbackComponent,
  'StockAndDeliveriesChart'
)

export {
  ChartWithErrorBoundary as StockAndDeliveriesChart,
  parseVolume,
  buildChartRows,
  seriesColour,
  shouldShowDeliveryMarker,
  pickAxisTicks,
}
